import { CheckCircle, Clock, Edit3, X, XCircle } from "lucide-react";
import { useState } from "react";
import { Markdown } from "@/components/shared/Markdown";
import type { FlowRun, FlowRunStep } from "@/types";

interface ReviewModalProps {
  flowRun: FlowRun;
  pausedStep: FlowRunStep;
  onApprove: (modifiedOutput?: string) => void;
  onReject: () => void;
  onClose: () => void;
  isSubmitting?: boolean;
}

export function ReviewModal({
  flowRun,
  pausedStep,
  onApprove,
  onReject,
  onClose,
  isSubmitting = false,
}: Readonly<ReviewModalProps>) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedOutput, setEditedOutput] = useState(pausedStep?.outputContent || "");

  // Safety check - if no paused step, show error
  if (!pausedStep) {
    return (
      <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
        <div className="bg-surface-primary border border-border-primary rounded-xl p-6 max-w-md">
          <p className="text-red-400">Error: No paused step found. Please try again.</p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-surface-secondary text-content-primary rounded-lg hover:bg-interactive-active"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const handleApprove = () => {
    if (isEditing && editedOutput !== pausedStep?.outputContent) {
      onApprove(editedOutput);
    } else {
      onApprove();
    }
  };

  return (
    <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-primary">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <Clock className="h-5 w-5 text-yellow-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-content-primary">Review Required</h2>
              <p className="text-sm text-content-tertiary">
                Step {pausedStep?.stepNumber ?? 0}: {pausedStep?.agentName ?? "Unknown"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1 text-content-tertiary hover:text-content-primary transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Step Input */}
          <div>
            <span className="block text-sm font-medium text-content-secondary mb-2">Step Input</span>
            <div className="p-4 bg-surface-secondary/50 rounded-lg border border-border-secondary max-h-48 overflow-y-auto">
              <Markdown content={pausedStep.inputContent || flowRun.inputPrompt || "No input"} />
            </div>
          </div>

          {/* Step Output */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="block text-sm font-medium text-content-secondary">Step Output</span>
              <button
                onClick={() => setIsEditing(!isEditing)}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-content-secondary hover:text-content-primary bg-surface-secondary hover:bg-interactive-active rounded-lg transition-colors disabled:opacity-50"
              >
                <Edit3 className="h-3.5 w-3.5" />
                {isEditing ? "Cancel Edit" : "Edit Output"}
              </button>
            </div>
            {isEditing ? (
              <textarea
                value={editedOutput}
                onChange={(e) => setEditedOutput(e.target.value)}
                disabled={isSubmitting}
                rows={10}
                className="w-full px-4 py-3 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary resize-none disabled:opacity-50"
                placeholder="Edit the output before approving..."
              />
            ) : (
              <div className="p-4 bg-surface-secondary/50 rounded-lg border border-border-secondary max-h-96 overflow-y-auto">
                <Markdown content={pausedStep.outputContent || "No output yet"} />
              </div>
            )}
          </div>

          {/* Token Usage */}
          {pausedStep && pausedStep.tokensUsed > 0 && (
            <div className="flex items-center gap-4 text-sm">
              <span className="text-content-quaternary">
                Tokens used: <span className="text-content-tertiary">{pausedStep.tokensUsed.toLocaleString()}</span>
              </span>
              {pausedStep.startedAt && pausedStep.completedAt && (
                <span className="text-content-quaternary">
                  Duration:{" "}
                  <span className="text-content-tertiary">
                    {Math.round(
                      (new Date(pausedStep.completedAt).getTime() - new Date(pausedStep.startedAt).getTime()) / 1000,
                    )}
                    s
                  </span>
                </span>
              )}
            </div>
          )}

          {/* Warning */}
          <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-sm text-yellow-300">
              <strong>Review this output carefully.</strong> Approving will continue the flow with this output.
              {isEditing && " Your edits will be used instead of the original output."}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-border-primary">
          <button
            onClick={onReject}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-4 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
          >
            <XCircle className="h-4 w-4" />
            Reject & Cancel
          </button>
          <button
            onClick={handleApprove}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-6 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            <CheckCircle className="h-4 w-4" />
            {isEditing && editedOutput !== pausedStep?.outputContent ? "Approve with Edits" : "Approve & Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
